const relationshipKey = (parentId, childId) => `${parentId}:${childId}`;

export function stripTransientNodeFields(node = {}) {
  const sharedNode = { ...node };
  delete sharedNode.autoFocus;
  delete sharedNode.animate;
  return sharedNode;
}

export function applyCreateNodeTransaction({ doc, yNodes, yRels, undoManager, command }) {
  const node = command?.node;
  const relationship = command?.relationship;

  if (!node?.id || !relationship?.parentId || relationship.childId !== node.id) {
    throw new Error('Invalid create-node command');
  }
  if (!yNodes.has(relationship.parentId)) {
    throw new Error(`Parent node ${relationship.parentId} does not exist`);
  }
  if (yNodes.has(node.id)) {
    throw new Error(`Node ${node.id} already exists`);
  }

  const { id, ...nodeData } = stripTransientNodeFields(node);
  const key = relationshipKey(relationship.parentId, relationship.childId);

  // Keep this user-visible command isolated from edits immediately before
  // or after it, while storing node and edge in one CRDT transaction.
  undoManager?.stopCapturing();
  doc.transact(() => {
    yNodes.set(id, nodeData);
    yRels.set(key, relationship);
  });
  undoManager?.stopCapturing();

  return id;
}

// Compatibility name for callers introduced in the first command slice.
export const applyCreateChildTransaction = applyCreateNodeTransaction;

function collectYjsSubtreeIds(nodeId, yRels) {
  const relationships = [...yRels.values()];
  const childrenByParent = new Map();
  relationships.forEach((relationship) => {
    if (!relationship?.parentId || !relationship?.childId) return;
    const children = childrenByParent.get(relationship.parentId) || [];
    children.push(relationship.childId);
    childrenByParent.set(relationship.parentId, children);
  });

  const nodeIds = [];
  const visited = new Set();
  const pending = [nodeId];
  while (pending.length) {
    const current = pending.shift();
    if (!current || visited.has(current)) continue;
    visited.add(current);
    nodeIds.push(current);
    pending.push(...(childrenByParent.get(current) || []));
  }
  return nodeIds;
}

export function applyDeleteSubtreeTransaction({ doc, yNodes, yRels, undoManager, command }) {
  if (command?.type !== 'delete-subtree' || !command.nodeId || !Array.isArray(command.nodeIds)) {
    throw new Error('Invalid delete-subtree command');
  }
  if (!command.nodeIds.length || new Set(command.nodeIds).size !== command.nodeIds.length) {
    throw new Error('Delete command requires unique branch node ids');
  }

  const currentNodeIds = collectYjsSubtreeIds(command.nodeId, yRels);
  const expectedIds = new Set(command.nodeIds);
  if (
    currentNodeIds.length !== expectedIds.size
    || currentNodeIds.some((nodeId) => !expectedIds.has(nodeId))
  ) {
    throw new Error('The branch changed before it could be deleted; try again');
  }
  const currentParentIds = [...yRels.values()]
    .filter((relationship) => relationship?.childId === command.nodeId)
    .map((relationship) => relationship.parentId);
  if (
    command.parentId
    && (currentParentIds.length !== 1 || currentParentIds[0] !== command.parentId)
  ) {
    throw new Error('The branch moved before it could be deleted; try again');
  }
  command.nodeIds.forEach((nodeId) => {
    if (!yNodes.has(nodeId)) throw new Error(`Node ${nodeId} does not exist`);
  });

  const deletingIds = new Set(command.nodeIds);
  undoManager?.stopCapturing();
  doc.transact(() => {
    command.nodeIds.forEach((nodeId) => yNodes.delete(nodeId));
    for (const [key, relationship] of yRels.entries()) {
      if (deletingIds.has(relationship?.parentId) || deletingIds.has(relationship?.childId)) {
        yRels.delete(key);
      }
    }
  });
  undoManager?.stopCapturing();
  return command.nextSelectedId || null;
}

function validatePositionCommand({ yNodes, command, expectedType = null }) {
  if (expectedType && command?.type !== expectedType) {
    throw new Error(`Invalid ${expectedType} command`);
  }
  if (!Array.isArray(command?.positions) || command.positions.length === 0) {
    throw new Error('Position command requires at least one node');
  }

  command.positions.forEach((entry) => {
    if (!entry?.id || !yNodes.has(entry.id)) {
      throw new Error(`Node ${entry?.id || ''} does not exist`);
    }
    if (!Number.isFinite(entry.position?.x) || !Number.isFinite(entry.position?.y)) {
      throw new Error(`Node ${entry.id} requires a finite position`);
    }
  });
}

function setCommandPositions(yNodes, command, lastEditedBy) {
  command.positions.forEach((entry) => {
    const current = yNodes.get(entry.id) || {};
    yNodes.set(entry.id, {
      ...current,
      position: entry.position,
      ...(entry.layout ? { layout: entry.layout } : {}),
      ...(lastEditedBy ? { lastEditedBy } : {}),
    });
  });
}

export function applyMoveNodesTransaction({
  doc,
  yNodes,
  undoManager,
  command,
  lastEditedBy = null,
  isolate = false,
}) {
  validatePositionCommand({ yNodes, command });
  if (isolate) undoManager?.stopCapturing();
  doc.transact(() => setCommandPositions(yNodes, command, lastEditedBy));
  if (isolate) undoManager?.stopCapturing();
  return command.nodeId;
}

export function beginUndoCaptureInteraction(undoManager, interactionRef) {
  if (!undoManager || !interactionRef || interactionRef.current) return false;
  undoManager.stopCapturing?.();
  interactionRef.current = {
    undoManager,
    captureTimeout: undoManager.captureTimeout,
  };
  // Direct-drag position transactions and the final release-settling
  // transaction form one user command even when the gesture lasts longer
  // than Yjs's default capture timeout.
  undoManager.captureTimeout = Number.MAX_SAFE_INTEGER;
  return true;
}

export function endUndoCaptureInteraction(interactionRef) {
  const interaction = interactionRef?.current;
  if (!interaction) return false;
  interaction.undoManager.stopCapturing?.();
  interaction.undoManager.captureTimeout = interaction.captureTimeout;
  interactionRef.current = null;
  return true;
}

export function applyReparentTransaction({
  doc,
  yNodes,
  yRels,
  undoManager,
  command,
  lastEditedBy = null,
  isolate = true,
}) {
  validatePositionCommand({ yNodes, command, expectedType: 'reparent-subtree' });
  const removed = command.removeRelationship;
  const added = command.relationship;
  if (!removed?.parentId || removed.childId !== command.nodeId) {
    throw new Error('Invalid relationship removal');
  }
  if (!added?.parentId || added.childId !== command.nodeId) {
    throw new Error('Invalid replacement relationship');
  }
  if (!yNodes.has(added.parentId)) {
    throw new Error(`Parent node ${added.parentId} does not exist`);
  }

  const oldKey = relationshipKey(removed.parentId, removed.childId);
  const newKey = relationshipKey(added.parentId, added.childId);
  if (!yRels.has(oldKey)) throw new Error('Existing parent relationship does not exist');

  if (isolate) undoManager?.stopCapturing();
  doc.transact(() => {
    setCommandPositions(yNodes, command, lastEditedBy);
    yRels.delete(oldKey);
    yRels.set(newKey, added);
  });
  if (isolate) undoManager?.stopCapturing();
  return command.nodeId;
}
